'use client'

import { toast } from 'sonner'

const API_BASE_URL = '/api'

export interface ApiError {
  error: string
  message: string
  details?: Record<string, unknown>
}

export class ApiRequestError extends Error {
  status: number
  errorCode: string
  details?: Record<string, unknown>

  constructor(
    message: string,
    status: number,
    errorCode: string,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.errorCode = errorCode
    this.details = details
  }

  getDisplayMessage(): string {
    switch (this.status) {
      case 400:
        return this.message || 'Invalid request. Please check your input.'
      case 401:
        return this.message || 'Invalid email or password.'
      case 500:
        return 'Server error. Please try again later.'
      case 0:
        return 'Network error. Please check your connection.'
      default:
        return this.message || 'An unexpected error occurred.'
    }
  }
}

type RequestOptions = RequestInit & {
  skipErrorToast?: boolean
}

/**
 * Centralized BFF client. Auth is cookie-based — never attach Bearer tokens from JS.
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { skipErrorToast = false, ...fetchOptions } = options
  const url = `${API_BASE_URL}${endpoint}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  }

  const config: RequestInit = {
    ...fetchOptions,
    headers,
    credentials: 'include',
  }

  try {
    const response = await fetch(url, config)
    let data: unknown

    try {
      data = await response.json()
    } catch {
      data = null
    }

    if (response.status === 401 && endpoint !== '/auth/login' && endpoint !== '/auth/login-totp') {
      window.dispatchEvent(new CustomEvent('auth:session-expired'))
    }

    if (!response.ok) {
      const error = data as ApiError | null
      const message = error?.message || 'An error occurred'

      if (!skipErrorToast) {
        toast.error(message)
      }

      throw new ApiRequestError(
        message,
        response.status,
        error?.error || 'UNKNOWN_ERROR',
        error?.details,
      )
    }

    return data as T
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error
    }

    const message = 'Network error. Please check your connection and try again.'
    if (!skipErrorToast) {
      toast.error(message)
    }

    throw new ApiRequestError(message, 0, 'NETWORK_ERROR')
  }
}

/** Same-origin fetch for editor/file routes — cookies carry auth automatically. */
export async function bffFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  return fetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}
